import React, { useState } from 'react';
import { Mail, MapPin, ExternalLink, ChevronRight, Users, HelpCircle } from 'lucide-react';
import { Button } from './ui/button';
import { ContactModal } from './ContactModal';
import { SupportModal } from './SupportModal';

export const Footer = ({ footerData, settings }) => {
  const currentYear = new Date().getFullYear();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Default values if footerData not loaded yet
  const tagline = footerData?.tagline || "Premium card protection for serious players";
  const copyrightText = footerData?.copyright_text || `© ${currentYear} Planeskeeper. All rights reserved.`;
  const buttonText = footerData?.distributor_button_text || "Become a Distributor";
  const disclaimerText = footerData?.disclaimer_text || "";
  const amazonLink = footerData?.amazon_link || "https://amazon.com";

  return (
    <>
      {/* Join Our Network Section */}
      <section className="py-20 relative overflow-hidden" style={{ marginTop: '3rem', backgroundColor: '#000000', borderTop: '1px solid rgb(255, 87, 34)' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          {/* Orange Glowing Brackets */}
          <div className="relative inline-block mb-6">
            <div className="absolute -left-16 top-1/2 -translate-y-1/2 text-6xl text-orange-500 opacity-80" style={{
              textShadow: '0 0 30px rgba(255, 87, 34, 0.8)'
            }}>
              [
            </div>
            <h2 className="gaming-title text-4xl sm:text-5xl font-bold text-white uppercase inline-block px-8">
              Join Our Network
            </h2>
            <div className="absolute -right-16 top-1/2 -translate-y-1/2 text-6xl text-orange-500 opacity-80" style={{
              textShadow: '0 0 30px rgba(255, 87, 34, 0.8)'
            }}>
              ]
            </div>
          </div>
          
          <div className="h-px w-48 bg-gradient-to-r from-transparent via-orange-500 to-transparent mx-auto mb-4"></div>
          <p className="gaming-text text-white uppercase tracking-wide mb-8" style={{ fontSize: '18px' }}>Wholesale Distribution</p>
          
          <Button
            onClick={() => setIsModalOpen(true)}
            size="lg"
            className="gaming-text bg-gradient-to-r from-[#FF4A00] to-[#FF4A00] hover:from-orange-500 hover:to-orange-600 text-white px-10 py-6 text-lg font-bold uppercase tracking-wider shadow-2xl shadow-orange-600/50 transition-all duration-300 transform hover:scale-105"
          >
            <Users className="w-5 h-5 mr-2" />
            {buttonText}
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="text-white" style={{ backgroundColor: '#000000', borderTop: '1px solid rgb(255, 87, 34)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid md:grid-cols-4 gap-8">
            {/* Logo and Tagline */}
            <div className="space-y-4">
              <img
                src={settings?.logo_url || "https://customer-assets.emergentagent.com/job_mtg-sleeves/artifacts/36nq3r5g_Artboard3%402x.png"}
                alt="Planeskeeper Accessories"
                className="h-12 w-auto"
              />
              <p className="gaming-text text-sm text-gray-400 leading-relaxed">
                {tagline}
              </p>
            </div>

            {/* Navigation */}
            <div>
              <h3 className="gaming-title text-white font-bold mb-4 uppercase tracking-wide text-sm">Navigation</h3>
              <ul className="space-y-2">
                <li>
                  <button
                    onClick={() => scrollToSection('products')}
                    className="gaming-text text-sm text-gray-400 hover:text-orange-400 transition-colors duration-200 flex items-center"
                  >
                    <ChevronRight className="w-4 h-4 mr-1" />
                    Products
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => scrollToSection('features')}
                    className="gaming-text text-sm text-gray-400 hover:text-orange-400 transition-colors duration-200 flex items-center"
                  >
                    <ChevronRight className="w-4 h-4 mr-1" />
                    Features
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => window.location.href = '/relics'}
                    className="gaming-text text-sm text-gray-400 hover:text-orange-400 transition-colors duration-200 flex items-center"
                  >
                    <ChevronRight className="w-4 h-4 mr-1" />
                    Relic Program
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => setIsModalOpen(true)}
                    className="gaming-text text-sm text-gray-400 hover:text-orange-400 transition-colors duration-200 flex items-center"
                  >
                    <ChevronRight className="w-4 h-4 mr-1" />
                    Contact
                  </button>
                </li>
              </ul>
            </div>

            {/* Shop */}
            <div>
              <h3 className="gaming-title text-white font-bold mb-4 uppercase tracking-wide text-sm">Shop</h3>
              <ul className="space-y-2">
                <li>
                  <a
                    href={amazonLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="gaming-text text-sm text-gray-400 hover:text-orange-400 transition-colors duration-200 flex items-center"
                  >
                    <ChevronRight className="w-4 h-4 mr-1" />
                    Buy on Amazon
                    <ExternalLink className="w-3 h-3 ml-1" />
                  </a>
                </li>
                <li>
                  <button
                    onClick={() => setIsModalOpen(true)}
                    className="gaming-text text-sm text-gray-400 hover:text-orange-400 transition-colors duration-200 flex items-center"
                  >
                    <ChevronRight className="w-4 h-4 mr-1" />
                    Wholesale Inquiry
                  </button>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h3 className="gaming-title text-white font-bold mb-4 uppercase tracking-wide text-sm">Contact</h3>
              <ul className="space-y-3">
                <li className="flex items-start space-x-2 gaming-text text-sm text-gray-400">
                  <Mail className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>info@planeskeeper.com</span>
                </li>
                <li className="flex items-start space-x-2 gaming-text text-sm text-gray-400">
                  <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>United States</span>
                </li>
                <li className="flex items-start space-x-2 gaming-text text-sm text-gray-400 mt-4">
                  <HelpCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <button
                    onClick={() => setIsSupportModalOpen(true)}
                    className="text-orange-500 hover:text-orange-400 underline"
                  >
                    Need Support?
                  </button>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t mt-12 pt-8" style={{ borderColor: '#ff5722' }}>
            <p className="gaming-text text-sm text-gray-500 uppercase tracking-wider text-center" style={{ marginBottom: '1rem' }}>
              {copyrightText}
            </p>
            {disclaimerText && (
              <p className="gaming-text text-xs text-gray-600 text-center max-w-4xl mx-auto">
                {disclaimerText}
              </p>
            )}
          </div>
        </div>
      </footer>

      {/* Contact Modal */}
      <ContactModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
      
      {/* Support Modal */}
      <SupportModal isOpen={isSupportModalOpen} onClose={() => setIsSupportModalOpen(false)} />
    </>
  );
};
